import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * Switching a **set** of questions on or off in one write.
 *
 * The ids are the subject, and the request is deliberately **not** scoped to a
 * section: a selection lives in whatever pane the operator is looking at, and a
 * section in the path would only be redundant with the ids while blocking a
 * pane that spans sections. The server applies the whole set or none of it — a
 * partial apply leaves the operator's selection half done with nothing on screen
 * saying which half.
 *
 * `isActive` is a strict boolean. `BooleanParamSchema` is the `'true' | 'false'`
 * transform for **query strings** and has no business in a body: a coerced
 * `'false'` here would switch on every question that was selected to switch off.
 */
export const BulkQuestionStatusSchema = z
  .object({
    /** The questions to write. */
    ids: z.array(UuidSchema).min(1).max(500),
    /** The state to put them all in. */
    isActive: z.boolean(),
  })
  .strict()
  .refine((body) => new Set(body.ids).size === body.ids.length, {
    message: 'The same id appears more than once.',
    path: ['ids'],
  });

export type BulkQuestionStatusRequest = z.infer<typeof BulkQuestionStatusSchema>;
